import React, { useEffect, useState } from 'react';
import { palettes, neutralPalettes } from '#lib/helpers/tailwind.js';
import IframePreview from '#@itinerary/iframe-preview.jsx';
import itinerary from '#src/itinerary.json';
import { useProfilesQueries } from '#hooks/use-itinerary-options.js';
import { useThemes } from '#hooks/use-themes.js';
import { LoadingIndicator, LoadingSkeleton } from '#lib/components/loading.jsx';
import ProfilesList from './profile-list.jsx';
import { useProfiles } from './use-profiles.jsx';
import { Detail } from './detail.jsx';
import toast from 'react-hot-toast';
import { ProfileButtons } from './profile-buttons.jsx';
import useDetail from './use-detail.js';
import useBlockNavigation from '#hooks/use-block-navigation.js';
import UnsavedChangesModal from '#components/unsaved-changes-modal.jsx';
import ManageControls from '#components/manage-controls.jsx';

export const colorValues = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

// PROFILE COMPONENT
function Profiles(props) {
    ////////////////////////////////
    // QUERIES

    const { itineraryOptions, isLoading, isRefetching, updateItineraryOptions, destroyItineraryOptions } =
        useProfilesQueries();

    const { themes } = useThemes();
    // const selectedTheme = themes?.find((theme) => theme.id === selectedProfile?.styles?.themeId) || themes?.[0];
    ////////////////////////////////

    ////////////////////////////////
    // STATES

    const [errors, setErrors] = useState({});

    // profiles reducer
    const profileProps = useProfiles(itineraryOptions ?? []);
    const {
        profiles,
        selectedProfile,
        selectedProfileIndex,
        addProfile,
        cloneProfile,
        deleteProfile,
        editProfile,
        editProfileColors,
        resetProfile,
        selectProfile
    } = profileProps;
    ////////////////////////////////

    ////////////////////////////////
    // HOOKS

    // block navigation
    const { proceedNavigation, blocked, reset, setUnsavedChanges, unsavedChanges } = useBlockNavigation();

    // detail
    const {
        handleChangeDropdown,
        handleChange,
        logo,
        defaultCover,
        defaultIntro,
        defaultJingle,
        customerContent,
        companyContent,
        supportedLanguages,
        primaryColors,
        neutralColors,
        mediaLogo,
        mediaCover,
        mediaIntro,
        mediaJingle,
        selectedTheme
    } = useDetail({
        themes,
        errors,
        selectedProfile,
        editProfile,
        editProfileColors,
        setErrors,
        setUnsavedChanges
    });

    ////////////////////////////////
    // HANDLERS

    // TODO: CAPIRE QUESTIONE ORDINAMENTO
    // useEffect(() => {
    //     if (!itineraryOptions || isRefetching) return;
    //     const index = itineraryOptions.findIndex((profile) => profile.id === selectedProfile.id);
    //     const defaultProfile = itineraryOptions.findIndex((profile) => profile.isDefault === true);
    //     selectProfile(index >= 0 ? index : defaultProfile);
    // }, [isRefetching]);

    const handleSelectProfile = (index) => {
        if (selectedProfileIndex === index) return;
        // NON MODIFICATO || CONFERMO => SELEZIONO
        if (!selectedProfile.isEditing || window.confirm(_t.global.saveReminder())) {
            selectProfile(index);
            // SE PROFILO SELEZIONATO => SCARTO I CHANGES
            // SE POI NON CI SARà UN PROFILO SELEZIONATO E CRASHA TUTTO CERCATE *GF*
            const dbProfile = itineraryOptions.find((el) => el.id === selectedProfile.id);
            if (dbProfile) resetProfile(selectedProfile.id, dbProfile);
            else deleteProfile(selectedProfile.id);
            setUnsavedChanges(false);
        }
    };

    const handleDeleteProfile = (profile) => {
        // prendo l'indice del profilo
        const index = profiles.findIndex((el) => el.id === profile.id);
        // se è default non lo cancello
        if (index >= 0 && profiles[index].isDefault) {
            toast.error(_t.profiles.cannotDelete());
            return;
        }
        profileProps && deleteProfile(profile.id);
        // se è selezionato tolgo flag modifiche non salvate
        if (selectedProfile.id === profile.id) {
            setUnsavedChanges(() => false);
        }
        // se è su db lo cancello da db
        if (!profile.isNew) destroyItineraryOptions.mutate({ id: profile.id });

        if (index < selectedProfileIndex) {
            selectProfile(selectedProfileIndex - 1);
            return;
        }
        if (index === selectedProfileIndex && index === profiles.length - 1 && index !== 0)
            selectProfile(selectedProfileIndex - 1);
    };

    const handleSaveProfile = (profile) => {
        if (profiles.some((el) => el.id !== profile.id && el.id.startsWith('NEW'))) {
            const confirm = window.confirm(_t.global.saveReminder());
            if (!confirm) return;
        }
        if (profiles.find((el) => el.name === profile.name && el.id != profile.id))
            return toast.error(_t.profiles.duplicateName());
        delete profile.isNew;
        delete profile.isEditing;
        setUnsavedChanges(false);
        updateItineraryOptions.mutate(profile);
    };

    const handleAddProfile = () => {
        let confirm;
        if (selectedProfile?.isEditing) confirm = window.confirm(_t.global.saveReminder());

        if (confirm === false) return;

        addProfile();
        setUnsavedChanges(true);
        if (confirm) {
            const dbProfile = itineraryOptions.find((el) => el.id === selectedProfile.id);
            if (dbProfile) resetProfile(selectedProfile.id, dbProfile);
            else deleteProfile(selectedProfile.id);
            setUnsavedChanges(false);
        }
    };
    ////////////////////////////////

    useEffect(() => {
        if (profiles.length > 0) {
            selectProfile(profiles.length - 1);
        }
    }, [profiles.length]);

    if (isLoading)
        return (
            <LoadingSkeleton className="w-full h-screen flex items-center justify-center">
                <LoadingIndicator />
            </LoadingSkeleton>
        );

    const updatedItinerary = {
        ...itinerary,
        cover: selectedProfile?.defaultCover,
        intro: selectedProfile?.defaultIntro
    };

    return themes ? (
        <div className="px-1 flex flex-col lg:flex-row justify-between gap-y-2 md:gap-x-4 w-full h-full">
            <div className="fixed w-full h-[116px] bg-white top-0 left-0 z-50 shadow-sm">
                <ManageControls.BarSlot>
                    <p className="text-2xl"> {_t.edit.profiles()}</p>
                </ManageControls.BarSlot>
                <ProfileButtons
                    selectedProfile={selectedProfile}
                    updateItineraryOptions={updateItineraryOptions}
                    handleSaveProfile={handleSaveProfile}
                    handleDeleteProfile={handleDeleteProfile}
                    setErrors={setErrors}
                />
            </div>
            <section className="w-full lg:h-full pt-[117px] lg:flex">
                <div className="w-full lg:w-1/3 p-3 mb-4 lg:mb-0 flex flex-col gap-4 overflow-auto bg-neutral-100">
                    <ProfilesList
                        {...profileProps}
                        selectedProfileIndex={selectedProfileIndex}
                        selectedProfile={selectedProfile}
                        onAdd={handleAddProfile}
                        onClone={cloneProfile}
                        onDelete={handleDeleteProfile}
                        onSelect={handleSelectProfile}
                        unsavedChanges={unsavedChanges}
                    />
                </div>
                <Detail
                    {...profileProps}
                    profiles={itineraryOptions}
                    errors={errors}
                    key={selectedProfile?.id}
                    selectedProfile={selectedProfile}
                    themes={themes}
                    editProfile={editProfile}
                    handleChange={handleChange}
                    handleChangeDropdown={handleChangeDropdown}
                    logo={logo}
                    mediaLogo={mediaLogo}
                    defaultCover={defaultCover}
                    mediaCover={mediaCover}
                    defaultIntro={defaultIntro}
                    mediaIntro={mediaIntro}
                    defaultJingle={defaultJingle}
                    mediaJingle={mediaJingle}
                    customerContent={customerContent}
                    companyContent={companyContent}
                    supportedLanguages={supportedLanguages}
                    primaryColors={primaryColors}
                    neutralColors={neutralColors}
                    selectedTheme={selectedTheme}
                />
                <div
                    className="w-full h-full mt-2 lg:mt-0 lg:w-1/3 p-2 lg:p-0"
                // style={{ margin: 0, height: heightPreview, minHeight: 500 }}
                >
                    <IframePreview
                        src={`${MEDIA_URL}/media/theme/${selectedTheme?.id ?? DEFAULT_THEME_ID
                            }/source/client/index.html`}
                        className="relative border border-neutral-200 w-full"
                        style={{ height: '100%' }}
                        options={selectedProfile}
                        itinerary={updatedItinerary}
                    />
                </div>
            </section>
            <UnsavedChangesModal isOpen={blocked} onClose={reset} onConfirm={proceedNavigation} />
        </div>
    ) : null;
}

Profiles.defaultProps = {
    dropDown: false,
    palettes: palettes,
    neutralPalettes: neutralPalettes
};
export default Profiles;
